import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { trackEvent, trackConversion } from '../../lib/analytics';

const WHATSAPP_NUMBER = '5511969241378';

const buildWhatsAppUrl = (form: { name: string; phone: string; email: string; message: string }) => {
    const lines = [
        'Olá! Gostaria de agendar uma consulta.',
        '',
        `*Nome:* ${form.name}`,
        `*Telefone:* ${form.phone}`,
        `*E-mail:* ${form.email}`,
        form.message ? `*Resumo:* ${form.message}` : null,
    ].filter(Boolean).join('\n');
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines as string)}`;
};

export const ServiceSidebar = () => {
    const { slug } = useParams<{ slug: string }>();
    const [form, setForm] = useState({ name: '', phone: '', email: '', message: '' });
    const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
    const [waUrl, setWaUrl] = useState('');

    const field = (key: string, value: string) => setForm(prev => ({ ...prev, [key]: value }));

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const url = buildWhatsAppUrl(form);
        setWaUrl(url);
        // Só generate_lead aqui — é A conversão do formulário. Não disparar
        // conversion_click{method:form} junto (duplicaria a conversão no GA4/Ads).
        trackEvent('generate_lead', { form_id: 'service_sidebar' });
        const win = window.open(url, '_blank', 'noopener,noreferrer');
        setStatus(win ? 'success' : 'error');
    };

    return (
        <aside className="space-y-8 font-vanguard-sans">
            <div className="sticky top-24 space-y-8">

                {/* ── Contact Card ── */}
                <div className="bg-[#0A0A0A] border border-stone-800 shadow-2xl overflow-hidden">
                    <div className="h-[2px] bg-pacces-gold" />

                    <div className="px-8 py-7 border-b border-stone-800">
                        <div className="flex items-start gap-4">
                            <div className="w-[2px] h-12 bg-pacces-gold flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="text-lg font-bold text-white uppercase tracking-widest font-vanguard-display leading-tight">
                                    Fale com um<br />Especialista
                                </h3>
                                <p className="text-[12px] font-bold text-pacces-gold uppercase tracking-[0.25em] mt-2">
                                    Atendimento Confidencial
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="p-8">
                        {status === 'success' ? (
                            <div className="text-center py-8">
                                <div className="w-14 h-14 bg-pacces-gold/10 border border-pacces-gold/30 flex items-center justify-center mx-auto mb-5">
                                    <span className="material-icons text-pacces-gold text-2xl">check</span>
                                </div>
                                <h4 className="text-white font-bold font-vanguard-display uppercase tracking-widest mb-2">
                                    WhatsApp Aberto
                                </h4>
                                <p className="text-stone-400 text-xs leading-relaxed max-w-[200px] mx-auto">
                                    Sua mensagem foi preparada. Confirme o envio no WhatsApp.
                                </p>
                                <button
                                    onClick={() => setStatus('idle')}
                                    className="mt-7 text-pacces-gold text-[12px] font-bold uppercase tracking-widest border-b border-pacces-gold/30 hover:border-pacces-gold pb-0.5 transition-all"
                                >
                                    Nova mensagem
                                </button>
                            </div>
                        ) : status === 'error' ? (
                            <div className="text-center py-8">
                                <div className="w-14 h-14 bg-pacces-gold/10 border border-pacces-gold/30 flex items-center justify-center mx-auto mb-5">
                                    <span className="material-icons text-pacces-gold text-2xl">chat</span>
                                </div>
                                <h4 className="text-white font-bold font-vanguard-display uppercase tracking-widest mb-2">
                                    Falta um passo
                                </h4>
                                <p className="text-stone-400 text-xs leading-relaxed max-w-[200px] mx-auto mb-5">
                                    Não abrimos o WhatsApp automaticamente. Toque abaixo para enviar sua mensagem.
                                </p>
                                <a
                                    href={waUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => trackConversion('sp', 'whatsapp')}
                                    className="inline-flex items-center justify-center gap-2 bg-pacces-gold text-pacces-ebony font-bold px-6 py-3 text-[12px] tracking-[0.2em] uppercase hover:bg-white transition-all"
                                >
                                    <span className="material-icons text-sm">chat</span>
                                    Abrir o WhatsApp
                                </a>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-[12px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
                                        Nome Completo <span className="text-pacces-gold">*</span>
                                    </label>
                                    <input
                                        required
                                        type="text"
                                        placeholder="Seu nome"
                                        value={form.name}
                                        onChange={e => field('name', e.target.value)}
                                        className="w-full bg-stone-900 border border-stone-800 text-white text-base focus:border-pacces-gold transition-colors placeholder:text-stone-700 px-4 py-3 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[12px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
                                        Telefone / WhatsApp <span className="text-pacces-gold">*</span>
                                    </label>
                                    <input
                                        required
                                        type="tel"
                                        placeholder="(11) 9xxxx-xxxx"
                                        value={form.phone}
                                        onChange={e => field('phone', e.target.value)}
                                        className="w-full bg-stone-900 border border-stone-800 text-white text-base focus:border-pacces-gold transition-colors placeholder:text-stone-700 px-4 py-3 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[12px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
                                        E-mail <span className="text-pacces-gold">*</span>
                                    </label>
                                    <input
                                        required
                                        type="email"
                                        placeholder="email@empresa.com"
                                        value={form.email}
                                        onChange={e => field('email', e.target.value)}
                                        className="w-full bg-stone-900 border border-stone-800 text-white text-base focus:border-pacces-gold transition-colors placeholder:text-stone-700 px-4 py-3 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[12px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-2">
                                        Resumo do Caso{' '}
                                        <span className="text-stone-700 normal-case tracking-normal">(opcional)</span>
                                    </label>
                                    <textarea
                                        rows={3}
                                        placeholder="Descreva brevemente sua situação..."
                                        value={form.message}
                                        onChange={e => field('message', e.target.value)}
                                        className="w-full bg-stone-900 border border-stone-800 text-white text-base focus:border-pacces-gold transition-colors placeholder:text-stone-700 px-4 py-3 outline-none resize-none"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    className="w-full bg-pacces-gold text-pacces-ebony font-bold py-4 text-[12px] tracking-[0.3em] uppercase hover:bg-white transition-all duration-300 shadow-xl flex items-center justify-center gap-2 mt-2"
                                >
                                    <span className="material-icons text-sm">send</span>
                                    Iniciar Atendimento
                                </button>

                                <p className="text-[12px] text-stone-600 text-center leading-relaxed pt-1">
                                    Suas informações são tratadas com total sigilo e confidencialidade.
                                </p>
                            </form>
                        )}
                    </div>
                </div>

                {/* ── Nossas Áreas ── */}
                <div className="bg-white dark:bg-stone-900/40 border border-stone-200 dark:border-stone-800 p-8 shadow-sm">
                    <h3 className="text-sm font-bold mb-6 text-stone-900 dark:text-white uppercase tracking-[0.2em] border-b border-stone-100 dark:border-stone-800 pb-4 font-vanguard-display">
                        Nossas Áreas
                    </h3>
                    <nav className="space-y-2">
                        <a
                            className="flex items-center justify-between p-4 bg-stone-50 dark:bg-stone-800/20 text-[12px] font-bold uppercase tracking-widest text-vanguard-primary border-l border-vanguard-primary"
                            href="/#services"
                        >
                            Todas as Áreas
                            <span className="material-icons text-xs">arrow_forward</span>
                        </a>
                        {[
                            { name: 'Regularização Imobiliária', slug: 'imobiliario' },
                            { name: 'Operações Imobiliárias', slug: 'operacoes-imobiliarias' },
                            { name: 'Direito Bancário', slug: 'bancario' },
                            { name: 'Cível Massificado', slug: 'civel' },
                            { name: 'Cível Estratégico', slug: 'civel-estrategico' },
                        ].map((item) => {
                            const isActive = slug === item.slug;
                            return (
                                <a
                                    key={item.slug}
                                    href={`/areas/${item.slug}`}
                                    className={`flex items-center justify-between p-4 text-[12px] font-bold uppercase tracking-widest transition-colors border-l ${
                                        isActive
                                            ? 'text-vanguard-primary border-vanguard-primary bg-stone-50 dark:bg-stone-800/20'
                                            : 'text-stone-400 border-transparent hover:text-vanguard-primary hover:border-vanguard-primary/30'
                                    }`}
                                >
                                    {item.name}
                                    <span className="material-icons text-[12px]">arrow_forward</span>
                                </a>
                            );
                        })}
                    </nav>
                </div>

            </div>
        </aside>
    );
};
